import { Box, Flex, Grid, Modal, Text, Title } from "@mantine/core";
import { Dispatch, SetStateAction } from "react";

import { AddressForm } from "./components/AddressForm";
import { AddressesList } from "./components/AddressesList";
import { CallToAction } from "@/components/CallToAction";
import { PaymentStep } from "@/types/pages/form";
import { ShipmentQuotes } from "./components/ShipmentQuotes";
import { SignInForm } from "./components/SignInForm";
import { SignUpForm } from "./components/SignUpForm";
import styles from "./styles.module.scss";
import { useOrderForm } from "./hooks/useOrderForm";

interface OrderFormProps {
  activeStep: PaymentStep;
  nextStep: (step?: PaymentStep) => void;
  quoteId: number;
  setShippingAmount: (value: number) => void;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export function OrderForm({
  activeStep,
  nextStep,
  quoteId,
  setShippingAmount,
  setIsLoading,
}: OrderFormProps) {
  const {
    userAddresses,
    shipmentQuotes,
    isPaymentLinkOpened,
    selectedAddress,
    setSelectedAddress,
    selectedShipmentQuote,
    setSelectedShipmentQuote,
    signInForm,
    handleSubmitSignIn,
    signUpForm,
    handleSubmitSignUp,
    addressForm,
    handleSubmitAddress,
    accountType,
    setAccountType,
    isCreateAddressModalOpen,
    setIsCreateAddressModalOpen,
    handleFindAddressByCep,
    handleFindShipmentQuotes,
    handleAcceptShipmentQuote,
    handleOpenPaymentLink,
    handleConfirmPayment,
  } = useOrderForm({ nextStep, quoteId, setShippingAmount, setIsLoading });

  return (
    <>
      <Modal
        title="Cadastrar Endereço"
        opened={isCreateAddressModalOpen}
        onClose={() => setIsCreateAddressModalOpen(false)}
        closeOnClickOutside={false}
        closeOnEscape={false}
        styles={{ body: { paddingBottom: 30 } }}
      >
        <AddressForm
          addressForm={addressForm}
          handleFindAddressByCep={handleFindAddressByCep}
          onSubmit={
            addressForm.onSubmit(handleSubmitAddress, (err) =>
              console.log(err),
            ) as any
          }
        />
      </Modal>

      <Flex align="flex-end" justify="space-between" className={styles.header}>
        <img src="/logo/default-logo.svg" alt="Logo" />

        {activeStep === "account" && (
          <>
            {accountType == "signUp" ? (
              <Text className={styles.accountTypeText}>
                Já tem uma conta?{" "}
                <span onClick={() => setAccountType("signIn")}>Entrar</span>
              </Text>
            ) : (
              <Text className={styles.accountTypeText}>
                Novo por aqui?{" "}
                <span onClick={() => setAccountType("signUp")}>
                  Cadastre-se
                </span>
              </Text>
            )}
          </>
        )}
      </Flex>

      <Box className={styles.tab}>
        <Title>1. Crie sua conta</Title>
        <Text>Cadastre-se para acesso completo</Text>

        {accountType == "signIn" ? (
          <Grid
            component="form"
            onSubmit={signInForm.onSubmit(handleSubmitSignIn) as any}
            className={`${styles.tabContent} ${
              activeStep === "account" && styles.openedTabContent
            }`}
          >
            <SignInForm signInForm={signInForm} />
          </Grid>
        ) : (
          <Grid
            component="form"
            onSubmit={signUpForm.onSubmit(handleSubmitSignUp) as any}
            className={`${styles.tabContent} ${
              activeStep === "account" && styles.openedTabContent
            }`}
          >
            <SignUpForm signUpForm={signUpForm} />
          </Grid>
        )}
      </Box>

      <Box className={styles.tab}>
        <Title>2. Informações de Entrega</Title>
        <Text>
          Adicione o seu endereço de entrega para enviarmos os produtos Vinci.
        </Text>

        <Grid
          component="form"
          className={`${styles.tabContent} ${
            activeStep === "shipping" && styles.openedTabContent
          }`}
        >
          <Grid.Col span={{ md: 12 }}>
            <Title>Selecione um endereço de entrega:</Title>
          </Grid.Col>

          <Grid.Col span={{ md: 12 }}>
            <AddressesList
              selectedAddress={selectedAddress}
              setSelectedAddress={setSelectedAddress}
              userAddresses={userAddresses}
              handleOpenCreateAddressModal={() =>
                setIsCreateAddressModalOpen(true)
              }
            />
          </Grid.Col>

          <Grid.Col span={{ md: 12 }}>
            {selectedAddress !== null && (
              <ShipmentQuotes
                handleFindShipmentQuotes={handleFindShipmentQuotes}
                shipmentQuotes={shipmentQuotes}
                selectedShipmentQuote={selectedShipmentQuote}
                setSelectedShipmentQuote={setSelectedShipmentQuote}
                handleAcceptShipmentQuote={handleAcceptShipmentQuote}
              />
            )}
          </Grid.Col>
        </Grid>
      </Box>

      <Box className={styles.tab}>
        <Title>3. Informações de Pagamento</Title>
        <Text>Efetue o pagamento e comece seu tratamento com a Vinci!</Text>

        <Grid
          component="form"
          className={`${styles.tabContent} ${
            activeStep === "payment" && styles.openedTabContent
          }`}
        >
          <Grid.Col span={{ md: 12 }}>
            <CallToAction
              w="100%"
              type="button"
              onClick={handleOpenPaymentLink}
            >
              Acessar Link de Pagamento
            </CallToAction>
          </Grid.Col>

          <Grid.Col span={{ md: 12 }}>
            {isPaymentLinkOpened && (
              <Flex align="center" justify="center" mt={15}>
                <Text
                  component="span"
                  className={styles.alreadyPaidText}
                  onClick={handleConfirmPayment}
                >
                  Já Realizei o Pagamento
                </Text>
              </Flex>
            )}
          </Grid.Col>
        </Grid>
      </Box>
    </>
  );
}
